/**
 * @voltxampere/audio — Speaker V_pin → frequency mapping (Sprint 31-4)
 *
 * Map a speaker's pin voltage to oscillator frequency via log-scale
 * interpolation between an audible noise floor (50 mV → 200 Hz) and a
 * full-amplitude reference (5 V → 2000 Hz). Two decades of voltage
 * span the full audible range a typical educational speaker covers
 * (logic-level signals 0..5V driven by an MCU pin or op-amp).
 *
 * Sentinel rows (Sprint 31-4 frozen mappings):
 *   |V_pin| <= 50 mV   → frequency = 0 Hz (silent, AudioFrame.active=false)
 *   |V_pin| = 50 mV    → 200 Hz (at floor, edge case)
 *   |V_pin| = 0.5 V    → ~1100 Hz (geometric mid of [0.05, 5])
 *   |V_pin| = 5 V      → 2000 Hz (saturation ceiling)
 *   |V_pin| > 5 V      → 2000 Hz (clamped — Tone.js Oscillator caps at 5000)
 *
 * Volume scales linearly with |V_pin| / V_REFERENCE so a louder pin
 * drives a louder speaker. Caller (apps/web fx adapter) packs the
 * three primitives below into `ComponentAudioState`:
 *   { frequency, volume, active }
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const V_THRESHOLD = 0.05; // V floor — below this, speaker silent
const V_REFERENCE = 5.0; // V at which mapping reaches F_MAX
const F_MIN = 200; // Hz at V_THRESHOLD
const F_MAX = 2000; // Hz at V_REFERENCE
/** log10(V_REFERENCE / V_THRESHOLD) = log10(100) = 2, computed once. */
const LOG_DENOM = 2;
/**
 * Compute the oscillator frequency in Hz for a given pin voltage.
 *
 * Returns 0 (silent) below the noise floor; the caller treats 0 as
 * "skip this oscillator". Symmetric for negative input — the sign of
 * V_pin does not affect frequency (rectifier-like behavior).
 */
export function speakerFrequency(V_pin) {
    const absV = Math.abs(V_pin);
    if (absV <= V_THRESHOLD)
        return 0;
    if (absV >= V_REFERENCE)
        return F_MAX;
    const ratio = Math.log10(absV / V_THRESHOLD) / LOG_DENOM;
    return F_MIN + (F_MAX - F_MIN) * ratio;
}
/**
 * Compute the oscillator volume [0, 1] for a given pin voltage.
 *
 * Linear above the threshold; saturated at V_REFERENCE. Matches the
 * speaker's expected loudness curve — 1V drives a quieter beep, 5V
 * drives a fuller tone.
 */
export function speakerVolume(V_pin) {
    const absV = Math.abs(V_pin);
    if (absV <= V_THRESHOLD)
        return 0;
    return Math.min(1, absV / V_REFERENCE);
}
/**
 * True iff the speaker is currently audible (above the noise floor).
 * Caller uses this to set `ComponentAudioState.active`.
 */
export function speakerActive(V_pin) {
    return Math.abs(V_pin) > V_THRESHOLD;
}
//# sourceMappingURL=speaker-frequency.js.map